import { useMemo } from 'react';

import api from '../../api/axiosClient';
import useApi from '../../hooks/api/useApi';
import useMutate from '../../hooks/api/useMutate';

/**
 * Loyalty — what this restaurant gives people who keep coming back.
 *
 * One programme per restaurant: the screen offers three schemes and a
 * restaurant runs one, so this is a single object rather than a list.
 */
export function useLoyaltyProgram() {
  const { data, error, isLoading, refresh } = useApi('/loyalty', {
    revalidateOnFocus: false
  });

  return {
    program: data?.data ?? null,
    // Described by the server so the cards read the same in the web app and the
    // phone, rather than the copy being written out twice.
    schemes: data?.data?.schemes ?? [],
    error,
    isLoading,
    refresh
  };
}

/**
 * Saves the whole programme at once. Which scheme is running and how it is
 * configured are one decision, and half of it saved is a programme that would
 * reward the wrong thing.
 */
export function useUpdateLoyaltyProgram() {
  const { put } = useMutate('/loyalty');

  return { updateProgram: (payload) => put(payload) };
}

/**
 * What the booking form should offer this person.
 *
 * Two things in one answer: the programme, which decides whether the checkbox
 * is drawn at all, and where this particular phone number already stands, which
 * decides whether it reads "add them" or "already a member". Asking as they
 * type rather than finding out on submit — a form that shows the wrong control
 * first and corrects itself afterwards is worse than one that waits a moment.
 *
 * A partial phone number is simply not sent, so the programme still arrives and
 * the checkbox appears before anybody has finished typing.
 */
export function useLoyaltyCustomerStatus(phone) {
  const clean = String(phone ?? '').replace(/\D/g, '');
  const params = clean.length === 10 ? `?phone=${clean}` : '';

  const { data, error, isLoading } = useApi(`/loyalty/customer-status${params}`, {
    revalidateOnFocus: false,
    keepPreviousData: true
  });

  return {
    /** The offer in the restaurant's own numbers, or null when none is running. */
    offer: data?.data?.offer ?? null,
    /** none | pending | active | declined */
    status: data?.data?.status ?? 'none',
    member: data?.data?.member ?? null,
    error,
    isLoading
  };
}

/**
 * Who is collecting under one scheme.
 *
 * Asked per scheme rather than for "the current programme": the Member Details
 * screen has a tab each, and somebody part-way through a scheme the restaurant
 * has since switched away from should still be findable.
 */
export function useLoyaltyMembers(type, search) {
  const params = new URLSearchParams({ type });
  if (search) params.set('search', search);

  const { data, error, isLoading, refresh } = useApi(type ? `/loyalty/members?${params}` : null, {
    revalidateOnFocus: false,
    keepPreviousData: true
  });

  return {
    members: data?.data ?? [],
    meta: data?.meta ?? {},
    pagination: data?.pagination ?? { total: 0, current_page: 1, per_page: 25 },
    error,
    isLoading,
    refresh
  };
}

/**
 * Acting on one member, or on a selection.
 *
 * useMutate takes its URL at call time, so these are plain functions rather
 * than hooks with a fixed path — the id is only known when a row is acted on.
 */
export function useMemberActions() {
  // Memoised, and it has to be. This returned a fresh object with fresh
  // function identities on every render, so any caller putting one of these in
  // a useCallback or useEffect dependency list got a new dependency each time —
  // effect runs, sets state, re-renders, new identity, effect runs again. The
  // member history panel did exactly that and called the endpoint in a loop
  // until the token expired underneath it.
  //
  // No dependencies: every function below closes over nothing but its own
  // arguments, so one object is correct for the life of the component.
  return useMemo(
    () => ({
      updateMember: (id, payload) => api.put(`/loyalty/members/${id}`, payload).then((r) => r.data),
      // Who they are, not what their card says — name, number, email. These
      // live on the customer, so they are their own request rather than half
      // of one that also moves counters.
      updateMemberDetails: (id, payload) => api.put(`/loyalty/members/${id}/details`, payload).then((r) => r.data),
      suspendMember: (id, suspended) => api.post(`/loyalty/members/${id}/suspend`, { suspended }).then((r) => r.data),
      deleteMember: (id) => api.delete(`/loyalty/members/${id}`).then((r) => r.data),
      // One call for a selection rather than one per row: fifty requests where
      // half fail leaves a list nobody can reason about.
      bulkMembers: (action, ids) => api.post('/loyalty/members/bulk', { action, ids }).then((r) => r.data),

      // What a card was built from: the till's own bills, and the purchases
      // somebody recorded by hand — and every correction since.
      memberHistory: (id) => api.get(`/loyalty/members/${id}/history`).then((r) => r.data),
      addEntry: (id, payload) => api.post(`/loyalty/members/${id}/entries`, payload).then((r) => r.data),

      // Correcting a row never writes to the row itself. A bill is a paid
      // financial record — it was receipted and is in somebody's takings — so
      // what these change is how much of it the card counts, which is loyalty's
      // to decide. `kind` is 'bill' or 'entry'.
      amendRow: (memberId, kind, rowId, payload) =>
        api.put(`/loyalty/members/${memberId}/history/${kind}/${rowId}`, payload).then((r) => r.data),
      removeRow: (memberId, kind, rowId, reason) =>
        api.delete(`/loyalty/members/${memberId}/history/${kind}/${rowId}`, { data: { reason } }).then((r) => r.data),
      restoreRow: (memberId, kind, rowId) => api.post(`/loyalty/members/${memberId}/history/${kind}/${rowId}/restore`).then((r) => r.data)
    }),
    []
  );
}

/**
 * The menu items a free-item reward can point at.
 *
 * Its own endpoint rather than the POS one, which also computes tax,
 * additional charges and held counts for a till — none of which a dropdown
 * needs and all of which it would pay for. Search runs server-side and is
 * capped at 50, so a long menu never arrives whole.
 */
export function useLoyaltyMenuItems(search) {
  const { data, error, isLoading } = useApi(`/loyalty/menu-items${search ? `?search=${encodeURIComponent(search)}` : ''}`, {
    revalidateOnFocus: false,
    keepPreviousData: true
  });

  return { items: data?.data ?? [], error, isLoading };
}
